/**
 * B.5 — Abrir WhatsApp Desktop desde un numero.
 *
 * Convierte un numero de telefono en un enlace pulsable que abre la app de
 * WhatsApp Desktop (`whatsapp://send?phone=...`) y, si no esta instalada, cae en
 * `https://wa.me/...`, que abre WhatsApp Web o la app del movil. El operador
 * hoy tiene el numero como texto plano y lo copia a mano; esto le ahorra el
 * paso.
 *
 * CASO CLAVE: no todo identificador de sesion es un telefono marcable. En el
 * canal de clientes la sesion es un nombre de perfil de WhatsApp (`Angie`, un
 * emoji, un solo punto) y en leads puede ser un aviso de dispositivo vinculado.
 * Cuando lo que hay NO es un numero, no se ofrece enlace: se muestra el texto
 * plano tal cual. Un `whatsapp://send?phone=Angie` no abre nada y ademas
 * engana al operador haciendole creer que puede escribir.
 */

interface Props {
  /**
   * El telefono o identificador. Puede venir con espacios, guiones, parentesis o
   * prefijo (`+34 600 11 22 33`, `0034-600112233`), o no ser un numero en
   * absoluto (`Angie`, `34600112233@s.whatsapp.net`).
   */
  telefono: string | null | undefined
  /**
   * Que texto mostrar en el enlace. Por defecto, el propio telefono ya
   * normalizado con el prefijo internacional. Util para pintar el nombre y que
   * el enlace sea el numero, o al reves.
   */
  etiqueta?: string
  /** Clases extra para encajar el enlace en la tipografia de cada sitio. */
  className?: string
}

/**
 * Normaliza un numero a solo digitos con prefijo internacional, o devuelve
 * `null` si lo que llega no es un telefono marcable.
 *
 * Reglas, en orden:
 *  - Se quitan espacios, guiones, puntos, parentesis y un `+` inicial.
 *  - Si tras eso queda cualquier caracter que NO sea un digito (una arroba, una
 *    letra, un emoji), NO es un numero: se devuelve `null`. Es lo que separa un
 *    telefono de un nombre de perfil o de un id de dispositivo.
 *  - `00` inicial es el prefijo internacional a la europea (`0034...`): se
 *    convierte en el numero sin el, porque WhatsApp quiere el numero en formato
 *    E.164 sin `+` ni `00`.
 *  - Un numero espanol de 9 digitos que empieza por 6/7/8/9 se le antepone `34`.
 *    Es una heuristica deliberada y acotada: este panel es de una empresa
 *    espanola y sus leads son de aqui. Un numero que ya trae prefijo (11+
 *    digitos, o los 9 no encajan en el patron nacional) se deja como esta.
 */
export function normalizarTelefono(entrada: string | null | undefined): string | null {
  if (!entrada) return null

  const recortado = entrada.trim()
  if (recortado === '') return null

  // Se guarda si venia con `+` para no confundir el prefijo: `+34...` ya es
  // internacional y no se le debe anteponer nada.
  const teniaMas = recortado.startsWith('+')

  // Fuera todo lo que sea separador visual; queda lo demas para poder
  // rechazarlo si no son digitos.
  const limpio = recortado.replace(/[\s\-.()]/g, '').replace(/^\+/, '')

  // Si queda algo que no es un digito, no es un telefono: nombre de perfil,
  // id de dispositivo (`...@s.whatsapp.net`), emoji, etc.
  if (!/^\d+$/.test(limpio)) return null

  // `00` a la europea -> internacional sin ceros.
  if (limpio.startsWith('00')) {
    const sinCeros = limpio.slice(2)
    return sinCeros.length >= 8 ? sinCeros : null
  }

  // Ya venia con `+`: internacional, se respeta.
  if (teniaMas) return limpio.length >= 8 ? limpio : null

  // Numero nacional espanol de 9 digitos (movil o fijo): se le pone el 34.
  if (/^[6789]\d{8}$/.test(limpio)) return `34${limpio}`

  // Numeros demasiado cortos para ser marcables se descartan: un `123` no es un
  // telefono y un enlace a el solo confunde.
  if (limpio.length < 8) return null

  // Cualquier otro (ya lleva prefijo, o es un internacional sin `+`): se
  // respeta tal cual.
  return limpio
}

/**
 * Enlace a WhatsApp Desktop con fallback a wa.me.
 *
 * El href es `whatsapp://send?phone=...`: ese esquema lo captura la app de
 * escritorio (o la del movil). Como en un navegador sin la app ese esquema no
 * hace nada, se pone `wa.me` como respaldo en un `onClick` que abre la web si a
 * los pocos milisegundos la pagina no ha perdido el foco (senal de que la app
 * arranco). Es el patron habitual para "abrir app nativa o caer a la web" sin
 * depender de deteccion de plataforma, que es fragil.
 */
export function EnlaceWhatsApp({ telefono, etiqueta, className }: Props) {
  const numero = normalizarTelefono(telefono)

  // No es un numero marcable: texto plano, sin enlace. Se muestra lo que haya
  // (el identificador original) para no perder informacion.
  if (numero === null) {
    return <span className={className}>{etiqueta ?? telefono ?? ''}</span>
  }

  const textoVisible = etiqueta ?? numero
  const urlApp = `whatsapp://send?phone=${numero}`
  const urlWeb = `https://wa.me/${numero}`

  return (
    <a
      href={urlApp}
      // Etiqueta explicita: un lector de pantalla debe saber que esto abre un
      // chat, no que es solo un numero.
      aria-label={`Abrir WhatsApp con ${numero}`}
      data-url-web={urlWeb}
      onClick={(evento) => {
        // Fallback: si la app de escritorio no captura el esquema, el navegador
        // no navega a ningun sitio y el numero se queda sin abrir. Tras un
        // instante, si la pestana sigue visible (la app no tomo el foco), se
        // abre wa.me. `document.hidden` distingue "la app arranco y robo el
        // foco" de "no paso nada".
        const abrirWeb = () => {
          if (!document.hidden) window.open(urlWeb, '_blank', 'noopener,noreferrer')
        }
        // No se previene el click: el esquema `whatsapp://` debe intentarse.
        window.setTimeout(abrirWeb, 400)
        void evento
      }}
      className={className}
    >
      {textoVisible}
    </a>
  )
}
